import { Platform } from 'react-native';

/**
 * The public origin this build of the product is served from.
 *
 * Campaign links, checkout returns and anything else that has to name the product
 * by URL resolve it here instead of embedding a host, so a build can never point
 * marketing traffic or a payment return at another environment's domain.
 */

function sanitizeOrigin(value: string | undefined): string | undefined {
  if (!value) return undefined;

  try {
    const url = new URL(value);
    // A misconfigured variable must yield no URL at all rather than a link that
    // executes script or carries embedded credentials.
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) {
      return undefined;
    }
    return url.origin;
  } catch {
    return undefined;
  }
}

export function resolvePublicWebOrigin(): string | undefined {
  // On web the browser is the authority: whichever host actually served the app
  // is the environment, whatever a build-time variable happens to claim.
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  // EXPO_PUBLIC_CHECKOUT_RETURN_ORIGIN was the first env-parameterised public
  // origin and is still the only one some deploys set.
  return sanitizeOrigin(
    process.env.EXPO_PUBLIC_WEB_URL || process.env.EXPO_PUBLIC_CHECKOUT_RETURN_ORIGIN,
  );
}

export function buildPublicWebUrl(path: string): string | undefined {
  const origin = resolvePublicWebOrigin();
  if (!origin) return undefined;

  try {
    return new URL(path, origin).toString();
  } catch {
    return undefined;
  }
}
